import { Checkbox, ListItemText, TextField } from "@mui/material";
import * as React from "react";
import { useState } from "react";
import MenuItem from "@mui/material/MenuItem";
import { COLOR_BILLABLE, COLOR_BILLED, COLOR_REPLACED, COLOR_SENT } from "../constants/EnelxColors";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import { STATE_RECHARGE_ACTIVE_FILTERS } from "../constants/EnelxRecoil";
import { ID_BILLABLE, ID_BILLED, ID_REJECTED, ID_REPLACED, ID_SENT } from "../constants/EnelxStatuses";
import {
    ID_FILTER_RECHARGE_CUSTOMER,
    ID_FILTER_RECHARGE_PLACE,
    ID_FILTER_RECHARGE_PRIVATE,
    ID_FILTER_RECHARGE_PUBLIC,
    ID_FILTER_RECHARGE_RFID,
    ID_FILTER_RECHARGE_STATIONTYPE,
    ID_FILTER_RECHARGE_STATUSES,
    ID_FILTER_SEARCH, ID_FILTER_RECHARGE_DEALERS, ID_FILTER_RECHARGE_LEGAL_ENTITIES
} from "../constants/EnelxFilters";
import * as Yup from "yup";
import AsyncAutocomplete from "../../../component/AsyncAutocomplete";
import AdvancedFilter from "../../../component/AdvancedFilter";
import ColouredDot from "../../../component/ColouredDot";
import { addFilter } from "../../../utils/FilterUtils";
import { useRechargeRepository } from "../../../api/repositories/RechargeRepository";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";

function RechargesFilter() {

    const { t } = useTranslation('finance', { keyPrefix: 'enelx' });
    const { t: tg } = useTranslation("translation");
    
    // Use Context
    const {
    globalData: { user },
    } = React.useContext(GlobalContext);

    // Setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: any[] = user.legalEntities
    ?.filter((x: any) => x.name.toLowerCase() !== "all")
    .map((x: any) => ({
        value: x.name,
        label: tg(`legalEntities.${x.name}`),
    }));

    const dealerOptions: any[] = user.dealers
    ?.filter((x: any) => x.name.toLowerCase() !== "all")
    .map((x: any) => ({
        value: x.name,
        label: tg(`dealers.${x.name}`),
    }));

    const { getPlaces } = useRechargeRepository();

    const setFilters = useSetRecoilState(STATE_RECHARGE_ACTIVE_FILTERS);
    const [dealers, setDealers] = useState<any[]>(dealerOptions);
    const [search, setSearch] = useState("");

    const schema = Yup.object().shape({
        customer: Yup.string().nullable(),
        rfidCard: Yup.string().max(10).nullable(),
        place: Yup.string().nullable(),
        stationType: Yup.string().nullable(),
        selectedStatuses: Yup.array(),
        dealers: Yup.array(),
        legalEntities: Yup.array().min(1, tg('validation.selectAtLeastOne'))
    });

    const defaultValues = {
        customer: "", rfidCard: "", place: "", stationType: "", selectedStatuses: [],
        legalEntities:legalEntityOptions.map((x: any) => x.value),
        dealers: [],
    };


    const renderCustomer = (field: any) => (
        <TextField
            margin="dense"
            id={"customer"}
            value={field.value}
            onChange={field.onChange}
            label={t('filters.recharges.' + ID_FILTER_RECHARGE_CUSTOMER)}
            fullWidth
            variant="standard"
        />
    );

    const renderLicensePlate = (field: any) => (
        <TextField
            margin="dense"
            id="license-plate"
            label={t('filters.recharges.license-plate')}
            fullWidth
            variant="standard"
            value={""}
            disabled
            inputProps={{ maxLength: 10 }}
        />
    );

    const renderPlace = (field: any) => (
        <AsyncAutocomplete
            id={"place"}
            callback={it => getPlaces(it).then(it => it.result)}
            value={field.value}
            onChange={field.onChange}
            renderInput={(params) => <TextField{...params}
                                               label={t('filters.recharges.' + ID_FILTER_RECHARGE_PLACE)}
            />}
        />
    );

    const renderRfidCard = (field: any, error: any) => (
        <TextField margin="dense" fullWidth
                   label={t('filters.recharges.' + ID_FILTER_RECHARGE_RFID)}
                   variant="standard"
                   value={field.value}
                   onChange={(e: any) => {
                       if (e.target.value === '' || e.target.value.charAt(e.target.value.length - 1).match(/\d/g)) {
                           field.onChange(e.target.value);
                       }
                   }}
                   inputProps={{ maxLength: 10 }}
                   error={error !== undefined}
                   helperText={error ? error.message : ""}
        />
    );

    const renderStationTypes = (field: any) => (
        <TextField fullWidth
                   select
                   label={t('filters.recharges.' + ID_FILTER_RECHARGE_STATIONTYPE)}
                   value={field.value}
                   onChange={field.onChange}
        >
            <MenuItem value={""}>
                <em/>
            </MenuItem>
            {stationTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                    {option.label}
                </MenuItem>
            ))}
        </TextField>
    );

    const renderStatuses = (field: any) => (
        <TextField fullWidth
                   label={t('filters.recharges.' + ID_FILTER_RECHARGE_STATUSES)}
                   select
                   SelectProps={{
                       multiple: true,
                       renderValue: (selected: any) => selected.map((s: any) => statuses.find(ss => ss.value === s)!.label).join(', ')
                   }}
                   value={field.value}
                   onChange={field.onChange}
        >
            {statuses.map((status) =>
                <MenuItem key={status.value} value={status.value}>
                    <Checkbox checked={field.value.indexOf(status.value) > -1}/>
                    <ColouredDot color={status?.color}/>
                    <ListItemText primary={status.label}/>
                </MenuItem>
            )}
        </TextField>
    )

    const renderMultiSelect = (label: string, list: any[], name: string, isHidden: boolean, onChange?: any) => {
        return (field: any, error: any, formState: any, watchAll: any, setValue: any) => (
          <TextField
            name={name}
            fullWidth
            select
            hidden={isHidden}
            disabled={list.length==0}
            SelectProps={{
              multiple: true,
              renderValue: (selected: any) =>
                selected
                  .map((s: any) => list.find((ss) => ss.value === s)!.label)
                  .join(", "),
            }}
            label={label}
            value={field.value}
            onChange={(event) => {
              if (onChange) {
                onChange(event.target.value, setValue);
              }
              field.onChange(event);
            }}
            error={error !== undefined}
            helperText={error ? error.message : ""}
          >
            {list.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                <Checkbox checked={field.value?.indexOf(item.value) > -1} />
                <ListItemText primary={item.label} />
              </MenuItem>
            ))}
          </TextField>
        );
    };    
    const fields = {
        customer: renderCustomer,
        rfidCard: renderRfidCard,
        place: renderPlace,
        stationType: renderStationTypes,
        selectedStatuses: renderStatuses,
        legalEntities: renderMultiSelect(
            tg("shared.legalEntities"),
            legalEntityOptions,
            "legalEntities",
            legalEntityOptions.length <= 1,
            (value: any, setValue: any) => {
              setValue('dealers', []);
              let dealers = user?.dealers
                ?.filter(
                  (x: any) =>
                    value?.length > 0 &&
                    x.legalEntities.some((e: any) => value.includes(e.name)) &&
                    x.name.toLowerCase() !== "all"
                )
                .map((x: any) => {
                  return {
                    label: tg(`dealers.${x.name}`),
                    value: x.name,
                  };
                });
              setDealers(dealers);
            }
          ),
          dealers: renderMultiSelect(tg("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1)      
    }


    const statuses = [
        {
            value: ID_BILLABLE,
            label: t('statuses.' + ID_BILLABLE),
            color: COLOR_BILLABLE + '.transparent'
        },
        {
            value: ID_REJECTED,
            label: t('statuses.' + ID_REJECTED),
            color: 'neutral.light'
        },
        {
            value: ID_REPLACED,
            label: t('statuses.' + ID_REPLACED),
            color: COLOR_REPLACED + '.transparent'
        },
        {
            value: ID_SENT,
            label: t('statuses.' + ID_SENT),
            color: COLOR_SENT + '.transparent'
        },
        {
            value: ID_BILLED,
            label: t('statuses.' + ID_BILLED),
            color: COLOR_BILLED + '.transparent'
        },
    ];

    const stationTypes = [
        {
            value: "PUBLIC",
            label: t('filters.recharges.' + ID_FILTER_RECHARGE_PUBLIC),
        },
        {
            value: "PRIVATE",
            label: t('filters.recharges.' + ID_FILTER_RECHARGE_PRIVATE),
        }
    ];

    const submit = (data: any) => {
        addFilter(setFilters, ID_FILTER_SEARCH, search);
        addFilter(setFilters, ID_FILTER_RECHARGE_CUSTOMER, data.customer);
        addFilter(setFilters, ID_FILTER_RECHARGE_PLACE, data.place);
        addFilter(setFilters, ID_FILTER_RECHARGE_RFID, data.rfidCard);
        addFilter(setFilters, ID_FILTER_RECHARGE_STATIONTYPE, data.stationType);
        addFilter(setFilters, ID_FILTER_RECHARGE_STATUSES, data.selectedStatuses);
        addFilter(setFilters, ID_FILTER_RECHARGE_LEGAL_ENTITIES, data.legalEntities);
        addFilter(setFilters, ID_FILTER_RECHARGE_DEALERS, data.dealers);
    }

    const reset = () => {
        setFilters([]);
        setDealers(dealerOptions);
    }

    return (
        <AdvancedFilter schema={schema} fields={fields} defaultValues={defaultValues}
                        searchInput={search} setSearchInput={setSearch}
                        onSubmit={submit}
                        onReset={reset}
        />
    )
}

export default RechargesFilter;